import { useEffect, useRef } from 'react';
import { useAsyncEffect } from 'hooks/useAsyncEffectGuard';
import { createEirYamlSnapshot } from 'utils/export';
import { cloudSyncService, hashString } from 'services/cloudSync';
import { healthMemoryService } from 'services/healthMemory';
import type { EIRData, JournalNote, ChatJournalEntry } from 'types';

interface CloudSyncState {
  data: EIRData | null;
  journalNotes: JournalNote[];
  chatJournalEntries: ChatJournalEntry[];
  healthAnalysis: unknown;
  healthAnalysisGeneratedAt: string | null;
}

interface UseCloudSyncLifecycleParams {
  authUser: { id: string } | null;
  cloudSyncEnabled: boolean;
  state: CloudSyncState;
}

/**
 * Syncs EIR snapshot to cloud when user is authenticated and cloud sync is enabled.
 */
export function useCloudSyncLifecycle({
  authUser,
  cloudSyncEnabled,
  state,
}: UseCloudSyncLifecycleParams): void {
  const lastSyncedHashRef = useRef<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!authUser) {
      lastSyncedHashRef.current = null;
    }
  }, [authUser]);

  useAsyncEffect(async (isCancelled) => {
    if (!authUser || !cloudSyncEnabled || !state.data) {
      lastSyncedHashRef.current = null;
      return;
    }

    const syncSnapshot = async () => {
      const currentState = stateRef.current;
      if (!authUser || !currentState.data || isCancelled()) return;

      try {
        const healthMemoryItems = healthMemoryService.isEnabled()
          ? await healthMemoryService.loadItems()
          : undefined;
        if (isCancelled()) return;
        const yaml = createEirYamlSnapshot(currentState.data, currentState.journalNotes, healthMemoryItems);
        const checksum = await hashString(yaml);
        if (isCancelled()) return;
        if (checksum && checksum === lastSyncedHashRef.current) return;
        await cloudSyncService.storeEirBackup(authUser.id, yaml, checksum);
        if (!isCancelled()) {
          lastSyncedHashRef.current = checksum;
        }
      } catch (error) {
        if (!isCancelled()) {
          console.error('[CloudSync] Failed to store EIR snapshot', error);
        }
      }
    };

    await syncSnapshot();
    if (isCancelled()) return;
    const interval = window.setInterval(() => void syncSnapshot(), 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [
    authUser,
    cloudSyncEnabled,
    state.data,
    state.journalNotes,
    state.chatJournalEntries,
    state.healthAnalysis,
    state.healthAnalysisGeneratedAt,
  ]);
}
